import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

// App-wide toast, module-singleton style: any code (components or plain lib
// functions, e.g. a Firestore catch block) calls showToast()/showSaveError()
// and the single <Toaster /> mounted in App.tsx renders it. No context to
// thread through — important because most write sites live in deep, prop-bag
// components.

type Tone = 'error' | 'success';
interface ToastItem { id: number; message: string; tone: Tone }

let emit: ((t: ToastItem) => void) | null = null;
let nextId = 1;

export function showToast(message: string, tone: Tone = 'success') {
  if (emit) {
    emit({ id: nextId++, message, tone });
  } else {
    // Toaster not mounted (e.g. crash screen) — don't lose the signal.
    console.error('[toast:unmounted]', message);
  }
}

/**
 * For Firestore catch blocks: log the real error (console → Sentry) and tell
 * the coach their change didn't stick. `what` is the thing that failed, in
 * coach words — e.g. "That scouting update".
 */
export function showSaveError(what: string, error: unknown) {
  console.error(`${what} — save failed:`, error);
  showToast(`${what} didn't save. Check your connection and give it another swing.`, 'error');
}

export const Toaster = () => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    emit = (t) => {
      setToasts(prev => [...prev.slice(-2), t]); // keep at most 3 visible
      setTimeout(() => {
        setToasts(prev => prev.filter(x => x.id !== t.id));
      }, 5000);
    };
    return () => { emit = null; };
  }, []);

  return (
    // bottom-28 clears the tab bar; pointer-events only on the chips so the
    // overlay never blocks taps.
    <div className="fixed bottom-28 inset-x-0 z-[100] flex flex-col items-center gap-2 px-4 pointer-events-none">
      <AnimatePresence>
        {toasts.map(t => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            className={`pointer-events-auto max-w-sm text-center font-black text-sm px-5 py-3 rounded-2xl shadow-lg text-surface ${
              t.tone === 'error' ? 'bg-danger' : 'bg-ink'
            }`}
          >
            {t.message}
            <button
              onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
              className="ml-3 opacity-60 hover:opacity-100"
            >✕</button>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};
